import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, Mic } from 'lucide-react';
import { useAIHealthCompanionStore } from '@/stores/ai-health-companion-store';
import { cn } from '@/lib/utils';
import { GlassCard } from '@/components/ui/glass-card';
import CompanionHeader from './CompanionHeader';
import StreamingMessageBubble from './StreamingMessageBubble';

interface CompanionPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompanionPanel: React.FC<CompanionPanelProps> = ({ isOpen, onClose }) => {
  const {
    messages,
    panelMode,
    isLoading,
    sendMessageStreaming,
  } = useAIHealthCompanionStore();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const lastMessage = messages[messages.length - 1];
  const suggestions = lastMessage?.suggestions || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const messageText = inputValue.trim();
    setInputValue('');
    await sendMessageStreaming(messageText);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    setInputValue(suggestion);
    await sendMessageStreaming(suggestion);
  };

  const renderChatView = () => (
    <div className="flex flex-col h-full">
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <div className="text-center space-y-2">
              <p className="text-lg font-medium">Start a conversation</p>
              <p className="text-sm">Ask about your health, medications, or test results</p>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'flex',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            {message.role === 'user' ? (
              <div className="max-w-[80%]">
                <div className="bg-primary-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5">
                  <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            ) : (
              <div className="max-w-[85%]">
                <StreamingMessageBubble message={message} />
              </div>
            )}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestion Chips */}
      {suggestions.length > 0 && !isLoading && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestionClick(suggestion)}
                className="flex-shrink-0 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-700 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Bar */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your health..."
              disabled={isLoading}
              rows={1}
              className={cn(
                'w-full px-4 py-2.5 pr-12 resize-none rounded-2xl',
                'bg-gray-100 dark:bg-gray-800',
                'border border-gray-200 dark:border-gray-700',
                'text-gray-900 dark:text-gray-100',
                'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'max-h-32 overflow-y-auto'
              )}
              style={{ minHeight: '44px' }}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className={cn(
              'flex items-center justify-center w-10 h-10 rounded-full transition-all',
              inputValue.trim()
                ? 'bg-primary-600 hover:bg-primary-700 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label={inputValue.trim() ? 'Send message' : 'Voice input'}
          >
            {inputValue.trim() ? (
              <ArrowUp className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderInsightsView = () => (
    <div className="p-4">
      <GlassCard variant="elevated" padding="lg">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p className="text-lg font-medium">Insights coming soon</p>
          <p className="text-sm mt-2">
            Personalized health insights and recommendations will appear here
          </p>
        </div>
      </GlassCard>
    </div>
  );

  const renderVoiceView = () => (
    <div className="p-4">
      <GlassCard variant="elevated" padding="lg">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Mic className="w-12 h-12 mx-auto mb-4" />
          <p className="text-lg font-medium">Voice mode</p>
          <p className="text-sm mt-2">Voice controls coming soon</p>
        </div>
      </GlassCard>
    </div>
  );

  return (
    <>
      {/* Desktop Panel */}
      <div
        className={cn(
          'hidden lg:block fixed top-16 right-0 h-[calc(100vh-4rem)] w-[400px] z-40 transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="bg-white dark:bg-gray-950 border-l border-gray-200 dark:border-gray-800 shadow-xl flex flex-col h-full">
          <CompanionHeader onClose={onClose} />
          {panelMode === 'chat' && renderChatView()}
          {panelMode === 'insights' && renderInsightsView()}
          {panelMode === 'voice' && renderVoiceView()}
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white dark:bg-gray-950">
          <div className="flex flex-col h-full">
            <CompanionHeader onClose={onClose} />
            {panelMode === 'chat' && renderChatView()}
            {panelMode === 'insights' && renderInsightsView()}
            {panelMode === 'voice' && renderVoiceView()}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(CompanionPanel);
